<template>
  <v-col class="register default-slot" md="7" sm="12">
    <v-dialog v-model="dialog" width="500px">
      <v-card class="confirmation-modal">
        <v-img class="modal-icon" width="84" :src="thumbsUpImage" />
        <h2 class="mt-8 mb-8">
          Thank you, your litter registration request has been submitted. You
          will be notified of approval.
        </h2>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogError" width="500px">
      <v-card class="confirmation-modal">
        <h2 class="mt-8 mb-8">
          {{ errorMessage }}
        </h2>
      </v-card>
    </v-dialog>
    <div class="top-icons">
      <a class="back-icon" v-on:click="goBack">
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
      </a>
    </div>
    <h1 class="register-title">Litter Registration Application</h1>
    <v-form class="register-form" v-model="isValid">
      <div class="table-gray-box">
        <table>
          <tr v-for="sire in sireInfo" :key="sire.dogId">
            <td>Litter Registration - Sire {{ sire.dogId }}</td>
            <td>
              ${{ fee }}
              <v-tooltip bottom content-class="fee-table-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"><font-awesome-icon icon="info-circle" /></span>
                </template>
                <litter-fee-table :priceInfo="priceInfo" />
              </v-tooltip>
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td>${{ fee * sireInfo.length }}</td>
          </tr>
        </table>
      </div>
      <div class="table-gray-box">
        <h3>Owner Info</h3>
        <tr v-for="(co, idx) in litterOwners" :key="idx">
          <td>Owner #{{ idx + 1 }}:</td>
          <td>{{ co.firstName }} {{ co.lastName }}</td>
          <td>{{ co.isPrimary ? "- Primary Owner" : "" }}</td>
        </tr>
        <h3 v-if="selectedKennels.length" class="mt-3">Associated Kennels</h3>
        <tr v-if="selectedKennels.length" v-for="(k, idx) in availableKennels" :key="idx">
          <td v-if="selectedKennels.indexOf(k.kennelId) > -1">
            {{ k.name }} - {{ k.kennelId }}
          </td>
        </tr>
      </div>
      <div class="table-gray-box">
        <h3>Dam Info</h3>
        <tr>
          <td>Name</td>
          <td>{{ damInfo.name }}</td>
        </tr>
        <tr>
          <td>ASCA ID</td>
          <td>{{ damInfo.dogId }}</td>
        </tr>
        <tr>
          <td>Main Color</td>
          <td>{{ damInfo.mainColor }}</td>
        </tr>
      </div>
      <div class="table-gray-box">
        <h3>Sire Info</h3>
        <div v-for="(sire, idx) in sireInfo" :key="sire.dogId">
          <tr>
            <td>
              <b>Sire #{{ idx + 1 }}</b>
            </td>
          </tr>
          <tr>
            <td>Name</td>
            <td>{{ sire.name }}</td>
          </tr>
          <tr>
            <td>ASCA ID</td>
            <td>{{ sire.dogId }}</td>
          </tr>
          <tr>
            <td>Main Color</td>
            <td>{{ sire.mainColor }}</td>
          </tr>
        </div>
      </div>
      <div class="table-gray-box table-spacing">
        <h3>Puppy Info</h3>
        <div v-for="(siresPuppies, name, idx) in puppies" :key="idx">
          <tr>
            <th>Sex</th>
            <th>Main Color</th>
            <th>Is Dead/Culled?</th>
            <th>Sire ID</th>
          </tr>
          <tr v-for="(puppy, i) in siresPuppies" :key="i">
            <td>{{ puppy.sex }}</td>
            <td>{{ puppy.mainColor }}</td>

            <td>{{ puppy.isDead }}</td>
            <td>{{ name }}</td>
          </tr>
        </div>
      </div>   
      <v-row>
          <v-col>
              <v-alert v-if="isArtificialInsemination" type="warning">
                  Sire’s collection information and <span v-html="artificialInseminationLink" /> must be on file with ASCA prior to approval of the litter.
                  Please send semen collection documentation from collection facility as well as AI forms directly to <span v-html="registrarEmailLink" />.
              </v-alert>
          </v-col>
      </v-row>
      <signature class="mt-3" :signature="sig" v-on:update="sig = $event">
        <template v-slot:signatureText>
          <litter-signature />
        </template>
      </signature>
    </v-form>
    <v-overlay :value="processingRequest">
      <v-progress-circular indeterminate color="primary" :size="70" :width="7"></v-progress-circular>
    </v-overlay>
    <hosted-registration-payment
      :disabled="!isValid || !sig || processingRequest || !feesLoaded"
      buttonText="Register"
      v-on:updatePaymentInfo="($event) => {
        processingRequest = true;
        sendRegistrationRequest($event, sig);
      }"
    />
    <p class="steps">STEP <b>3</b> OF <b>3</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import Signature from "@/components/common/inputs/Signature.vue";
import LitterFeeTable from "@/components/Portal/LitterFeeTable.vue";
import LitterSignature from "@/components/common/LitterSignature.vue";
import FooterText from "@/components/common/FooterText.vue";
import HostedRegistrationPayment from "@/components/common/HostedRegistrationPayment.vue";
import { getErrorMessage, getErrorResponse } from "@/services/errorHandler";

import { UserInfo } from "@/types/User";

const thumbsUpImage = require('@/assets/thumbs-up-duotone.svg');
import { LitterRegistration } from "@/constants";

export default Vue.extend({
  name: "RegisterLitterForm3",
  components: {
    Signature,
    LitterFeeTable,
    LitterSignature,
    FooterText,
    HostedRegistrationPayment,
  },
  data: function () {
    return {
      thumbsUpImage,
      dialog: false,
      dialogError: false,
      showAllCoOwners: false,
      isValid: true,
      processingRequest: false,
      sig: this.$props.signature,
      availableKennels: this.$props.damInfo.ownersKennels,
      errorMessage: "",
      artificialInseminationLink: LitterRegistration.ArtificialInsemination_Link,
      registrarEmailLink: LitterRegistration.RegistrarEmail_Link,
    };
  },
  props: {
    changePage: Function,
    sendRegistrationRequest: Function,
    status: Object,
    damInfo: Object,
    updateSignature: Function,
    signature: String,
    sireInfo: Array,
    puppies: Object,
    fee: Number,
    selectedKennels: Array,
    priceInfo: Array,
    isArtificialInsemination: Boolean,
    feesLoaded: Boolean,
  },
  watch: {
    status: function (newVal: any, oldVal: any) {
      if (newVal?.success) {
        this.openModal();
      } else {
        this.errorMessage = getErrorMessage(newVal);
        this.openErrorModal();
      }
    },
    dialog: function (newVal: boolean, oldVal: boolean) {
      if (!newVal) {
        this.$store.dispatch("loadPersonTasks");
        this.$navigate("/");
      }
    },
    dialogError: function (newVal: boolean, oldVal: boolean) {
      if (!newVal && getErrorResponse(this.status)?.data?.paymentIssue) {
        this.processingRequest = false;
        this.$store.dispatch("loadPersonTasks");
        this.$navigate("/");
      } else if (!newVal) {
        this.processingRequest = false;
      }
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    litterOwners() {
      if (!!this.damInfo.lease) {
        return this.damInfo.lease.lesees;
      } else {
        return this.damInfo.primaryOwners;
      }
    },
  },
  methods: {
    goBack: function() {
      this.updateSignature(this.sig);
      this.changePage(2);
    },
    toggleShowAll: function () {
      this.$data.showAllCoOwners = !this.$data.showAllCoOwners;
    },
    openModal: function () {
      this.$data.dialog = true;
    },
    openErrorModal: function () {
      this.$data.dialogError = true;
    },
  },
});
</script>
